import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { AdminDashboardStats } from "@/lib/admin-dashboard";

/**
 * Ticket AD4a/AD7: Arbeitsvorrat der internen Startseite – genau eine
 * Serverabfrage. Die Zähllogik liegt in `admin-dashboard.server.ts`, damit
 * diese Datei eine dünne Hülle bleibt (Code-Teilung).
 */
export type {
  AdminDashboardStats,
  AdminWorkItem,
  AdminWorkItemKey,
  AdminWorkRole,
} from "@/lib/admin-dashboard";

export const getAdminDashboardStats = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<AdminDashboardStats> => {
    const { computeAdminDashboardStats } = await import("@/lib/admin-dashboard.server");
    return computeAdminDashboardStats(context.supabase, context.userId);
  });

/** Ticket AD4a: Nachbearbeitung eines Fragebogen-Rücklaufs markieren. */
export const setQuestionnaireReviewed = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { responseId: string; reviewed: boolean }) => input)
  .handler(async ({ context, data }) => {
    const { error } = await context.supabase
      .from("questionnaire_responses")
      .update(
        data.reviewed
          ? { reviewed_at: new Date().toISOString(), reviewed_by: context.userId }
          : { reviewed_at: null, reviewed_by: null },
      )
      .eq("id", data.responseId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Ticket AD4a: Produktanfrage aus dem 100-Tage-Modul als beantwortet markieren. */
export const setModuleInterestHandled = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { id: string; handled: boolean }) => input)
  .handler(async ({ context, data }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden: Admin role required");
    const { error } = await context.supabase
      .from("module_interest_registrations")
      .update(
        data.handled
          ? { handled_at: new Date().toISOString(), handled_by: context.userId }
          : { handled_at: null, handled_by: null },
      )
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
